// src/Support.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TourProps } from 'antd';
import { Alert, Input, Typography } from 'antd';
import {
  CreditCardOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  MailOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  TranslationOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiscord, faFacebook } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import {
  loadSupportArticles,
  SupportArticleResults,
  type CategorizedSupportArticle,
} from './SupportArticles';
import AppPageHeader from './components/AppPageHeader';
import { supportArticleCategories } from './supportArticles';
import { resolveFrontendApiBase } from './utils/apiBase';
import AppTour, { AppTourVideoLink, renderAppTourPanel } from './tours/AppTour';
import { tourTarget, useGuidedTour } from './tours/useGuidedTour';
import { supportTutorialsPath } from './tutorialVideoLinks';
import './Support.css';

const { Text } = Typography;
const API_BASE = resolveFrontendApiBase();

type Tutorial = {
  title: string;
  url: string;
  id: string;
  duration?: string;
};

type ContactChannel = {
  key: 'email' | 'discord' | 'facebook';
  title: string;
  description: string;
  href: string;
  cta: string;
  icon: React.ReactNode;
};

type FeaturedLink = {
  title: string;
  description: string;
  icon: React.ReactNode;
  articleSlug?: string;
  sectionId?: string;
};

type CategoryFilter = {
  key: string;
  label: string;
  categoryKeys: string[];
};

type SupportTabKey = 'articles' | 'tutorials' | 'faq' | 'guidelines';

const tutorials: Tutorial[] = [
  { title: 'How to Upload Your First Book in ScribeShadow', url: 'https://youtu.be/pyjYfmUPoiA', id: 'pyjYfmUPoiA' },
  { title: 'ScribeShadow Manage Books Tour', url: 'https://youtu.be/xCJtRkOWz6s', id: 'xCJtRkOWz6s' },
  { title: 'Original Manuscript Dashboard and Metadata', url: 'https://youtu.be/3jTIwJg5LKk', id: '3jTIwJg5LKk' },
  { title: 'How to Proofread Your Original Manuscript', url: 'https://youtu.be/CUXlGRSHXV8', id: 'CUXlGRSHXV8' },
  { title: 'Chapter-Level Controls in ScribeShadow', url: 'https://youtu.be/PTH7wTayLPo', id: 'PTH7wTayLPo' },
  { title: 'ScribeShadow Simple Workflow', url: 'https://youtu.be/eyPS4tOYG4E', id: 'eyPS4tOYG4E' },
  { title: 'Advanced Translation Workflow', url: 'https://youtu.be/8gM6Gd8Eon4', id: '8gM6Gd8Eon4' },
  { title: 'Translated Language Dashboard', url: 'https://youtu.be/E2jJtXwmKu0', id: 'E2jJtXwmKu0' },
  { title: 'How to Proofread a Translated Manuscript', url: 'https://youtu.be/0YZGhxz_eNA', id: '0YZGhxz_eNA' },
];

const advancedTutorials: Tutorial[] = [
  { title: 'Chapter Editing in ScribeShadow', url: 'https://youtu.be/fPyt_h-ciUo', id: 'fPyt_h-ciUo' },
  { title: 'ScribeShadow Glossary Tutorial', url: 'https://youtu.be/JzIgCCP-zmA', id: 'JzIgCCP-zmA' },
];

const contactChannels: ContactChannel[] = [
  {
    key: 'email',
    title: 'Email',
    description: 'Best for account, billing, or book-specific questions.',
    href: 'mailto:support@scribeshadow.com',
    cta: 'Email support',
    icon: <FontAwesomeIcon icon={faEnvelope} />,
  },
  {
    key: 'discord',
    title: 'Discord',
    description: 'Quick help from the team and author community.',
    href: 'https://discord.com/invite/k7vnT38vPq',
    cta: 'Open Discord',
    icon: <FontAwesomeIcon icon={faDiscord} />,
  },
  {
    key: 'facebook',
    title: 'Facebook',
    description: 'Ask questions and share workflow tips with other authors.',
    href: 'https://www.facebook.com/groups/216351367578868/',
    cta: 'Visit group',
    icon: <FontAwesomeIcon icon={faFacebook} />,
  },
];

const featuredLinks: FeaturedLink[] = [
  {
    title: 'Job looks stuck',
    description: 'Check Processing Status and what to do before contacting support.',
    icon: <ReloadOutlined />,
    articleSlug: 'faq-why-a-job-looks-stuck',
  },
  {
    title: 'Credits look wrong',
    description: 'Review credit balances, locked credits, and recent billing updates.',
    icon: <CreditCardOutlined />,
    articleSlug: 'faq-credits-look-wrong',
  },
  {
    title: 'Upload failed',
    description: 'Check file type, structure, and common upload blockers.',
    icon: <ExclamationCircleOutlined />,
    articleSlug: 'faq-why-file-upload-failed',
  },
  {
    title: 'Translate unavailable',
    description: 'Find the setup issues that can make Translate unavailable.',
    icon: <TranslationOutlined />,
    articleSlug: 'faq-why-translate-is-blocked',
  },
  {
    title: 'Replace manuscript',
    description: 'Know when replacement is available and what can block it.',
    icon: <FileTextOutlined />,
    articleSlug: 'replace-a-manuscript',
  },
  {
    title: 'Contact support',
    description: 'Reach us by email or community channels.',
    icon: <MailOutlined />,
    sectionId: 'support-contact',
  },
];

const categoryFilters: CategoryFilter[] = [
  {
    key: 'all',
    label: 'All',
    categoryKeys: [],
  },
  {
    key: 'library',
    label: 'Library',
    categoryKeys: ['library-books'],
  },
  {
    key: 'translation',
    label: 'Translation',
    categoryKeys: ['translation-proofreading'],
  },
  {
    key: 'billing',
    label: 'Billing',
    categoryKeys: ['account-billing'],
  },
  {
    key: 'troubleshooting',
    label: 'Troubleshooting',
    categoryKeys: ['faqs-troubleshooting'],
  },
  {
    key: 'metadata',
    label: 'Metadata',
    categoryKeys: ['metadata-marketing'],
  },
  {
    key: 'exports',
    label: 'Exports',
    categoryKeys: ['audiobooks-exports'],
  },
];

const supportPrepItems = [
  'account email',
  'book title',
  'support code',
];

const legacyTargetByValue: Record<string, string> = {
  articles: 'support-articles',
  faq: 'support-faq',
  tutorials: 'support-tutorials',
  guidelines: 'word-formatting-guidelines',
  videoTutorial: 'support-tutorials',
  'word-formatting-guidelines': 'word-formatting-guidelines',
};

const supportTabByTargetId: Record<string, SupportTabKey> = {
  'support-search': 'articles',
  'support-featured': 'articles',
  'support-articles': 'articles',
  'support-faq': 'faq',
  'support-tutorials': 'tutorials',
  'word-formatting-guidelines': 'guidelines',
  'support-contact': 'guidelines',
};

const supportTourStepTargetIds = [
  'support-search',
  'support-featured',
  'support-articles',
  'support-tutorials',
  'support-contact',
] as const;

const getLegacyTargetId = (location: ReturnType<typeof useLocation>) => {
  const params = new URLSearchParams(location.search);
  const tab = params.get('tab');
  if (tab && legacyTargetByValue[tab]) {
    return legacyTargetByValue[tab];
  }
  const hash = location.hash.replace('#', '');
  if (hash && legacyTargetByValue[hash]) {
    return legacyTargetByValue[hash];
  }
  return undefined;
};

const getRequestedArticleSlug = (
  location: ReturnType<typeof useLocation>,
): string | null | undefined => {
  const rawSlug = new URLSearchParams(location.search).get('article');
  if (rawSlug === null) return undefined;
  const slug = rawSlug.trim().toLowerCase();
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) ? slug : null;
};

const areStringArraysEqual = (a: string[], b: string[]) => (
  a.length === b.length && a.every((value, index) => value === b[index])
);

interface SupportProps {
  tourUserId?: string;
}

const Support: React.FC<SupportProps> = ({ tourUserId }) => {
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [articles, setArticles] = useState<CategorizedSupportArticle[]>([]);
  const [activeSlug, setActiveSlug] = useState<string | undefined>();
  const [highlightedArticleSlug, setHighlightedArticleSlug] = useState<string | undefined>();
  const [activeCategoryFilter, setActiveCategoryFilter] = useState('all');
  const [activeSupportTab, setActiveSupportTab] = useState<SupportTabKey>('articles');
  const [openArticleCategories, setOpenArticleCategories] = useState<string[]>([]);
  const previousDeepLinkSearch = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const supportTour = useGuidedTour({
    userId: tourUserId,
    apiBase: API_BASE,
    flag: 'supportPageTourSeen',
    enabled: true,
    autoStart: false,
    ready: !loading,
  });
  const {
    start: startTour,
    setCurrent: setTourCurrent,
    open: tourOpen,
    current: tourCurrent,
  } = supportTour;
  const supportTourSteps = useMemo<TourProps['steps']>(() => ([
    {
      title: 'Search the help center',
      description: renderAppTourPanel('Use the search box to find articles across uploads, credits, translation, exports, and troubleshooting.'),
      target: tourTarget('support-search'),
    },
    {
      title: 'Start with common answers',
      description: renderAppTourPanel('Top answers jump directly to the issues authors ask about most often.'),
      target: tourTarget('support-featured'),
    },
    {
      title: 'Browse article categories',
      description: renderAppTourPanel('Category filters group articles by workflow so you can scan without searching.'),
      target: tourTarget('support-articles'),
    },
    {
      title: 'Watch tutorials',
      description: renderAppTourPanel(
        <>
          Video tutorials walk through common workflows in more detail, with advanced topics grouped separately.
          <AppTourVideoLink href={supportTutorialsPath} newTab={false}>
            Browse all video tutorials
          </AppTourVideoLink>
        </>
      ),
      target: tourTarget('support-tutorials'),
    },
    {
      title: 'Contact support',
      description: renderAppTourPanel('When you need help, include account email, book title, and any support code shown in the app.'),
      target: tourTarget('support-contact'),
    },
  ]), []);

  const showTabForTourStep = useCallback((stepIndex: number) => {
    const targetId = supportTourStepTargetIds[stepIndex];
    if (!targetId) return;
    const targetTab = supportTabByTargetId[targetId];
    if (targetTab) {
      setActiveSupportTab(targetTab);
    }
  }, []);

  const startSupportTour = useCallback(() => {
    showTabForTourStep(0);
    startTour();
  }, [showTabForTourStep, startTour]);

  const handleSupportTourChange = useCallback((nextStep: number) => {
    showTabForTourStep(nextStep);
    setTourCurrent(nextStep);
  }, [showTabForTourStep, setTourCurrent]);

  useEffect(() => {
    if (!tourOpen) return;
    showTabForTourStep(tourCurrent);
  }, [showTabForTourStep, tourCurrent, tourOpen]);

  useEffect(() => {
    let isActive = true;
    const load = async () => {
      try {
        const results = await loadSupportArticles();
        if (isActive) {
          setArticles(results);
          setLoading(false);
        }
      } catch {
        if (isActive) {
          setError('Unable to load support articles right now.');
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    const targetId = getLegacyTargetId(location);
    if (!targetId) return;
    const targetTab = supportTabByTargetId[targetId];
    if (targetTab) {
      setActiveSupportTab(targetTab);
    }
    window.setTimeout(() => {
      const target = document.getElementById(targetId);
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      target?.focus({ preventScroll: true });
    }, 0);
  }, [location]);

  useEffect(() => {
    const requestedSlug = getRequestedArticleSlug(location);
    if (requestedSlug === undefined) {
      if (previousDeepLinkSearch.current !== null) {
        setActiveSlug(undefined);
        setHighlightedArticleSlug(undefined);
      }
      previousDeepLinkSearch.current = null;
      return;
    }
    previousDeepLinkSearch.current = location.search;

    setActiveSupportTab('articles');
    setQuery('');
    setActiveCategoryFilter('all');
    if (loading) return;

    const matchingArticle = requestedSlug
      ? articles.find((article) => article.slug === requestedSlug)
      : undefined;
    setActiveSlug(matchingArticle?.slug);
    setHighlightedArticleSlug(matchingArticle?.slug);
  }, [articles, loading, location.search]);

  useEffect(() => {
    if (!highlightedArticleSlug) return undefined;
    const timer = window.setTimeout(() => {
      setHighlightedArticleSlug(undefined);
    }, 3500);
    return () => window.clearTimeout(timer);
  }, [highlightedArticleSlug]);

  const filteredArticles = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter((article) => article.searchText.includes(q));
  }, [articles, query]);

  const selectedCategoryFilter = categoryFilters.find((filter) => filter.key === activeCategoryFilter) || categoryFilters[0];
  const visibleArticles = useMemo(() => {
    if (query.trim() || selectedCategoryFilter.key === 'all') {
      return filteredArticles;
    }
    return filteredArticles.filter((article) => selectedCategoryFilter.categoryKeys.includes(article.categoryKey));
  }, [filteredArticles, query, selectedCategoryFilter]);
  const faqArticles = useMemo(
    () => articles.filter((article) => article.categoryKey.includes('faq') || article.slug.startsWith('faq-')),
    [articles],
  );

  const resultLabel = loading
    ? 'Loading articles...'
    : query.trim()
      ? `${visibleArticles.length} matching article${visibleArticles.length === 1 ? '' : 's'}`
      : selectedCategoryFilter.key === 'all'
        ? `${articles.length} support articles`
        : `${visibleArticles.length} ${selectedCategoryFilter.label.toLowerCase()} article${visibleArticles.length === 1 ? '' : 's'}`;

  const openArticle = (slug?: string, sectionId = 'support-articles') => {
    setQuery('');
    setActiveCategoryFilter('all');
    setActiveSupportTab('articles');
    setActiveSlug(slug);
    setHighlightedArticleSlug(slug);
    window.setTimeout(() => {
      const target = document.getElementById(sectionId);
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      target?.focus({ preventScroll: true });
    }, 0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setHighlightedArticleSlug(undefined);
    if (event.target.value.trim()) {
      setActiveCategoryFilter('all');
    }
  };

  const setCategoryFilter = (filterKey: string) => {
    setActiveCategoryFilter(filterKey);
    setActiveSlug(undefined);
    setHighlightedArticleSlug(undefined);
  };

  const handleActiveSlugChange = (slug?: string) => {
    setActiveSlug(slug);
    if (slug !== highlightedArticleSlug) {
      setHighlightedArticleSlug(undefined);
    }
  };

  const defaultOpenCategories = useMemo(() => {
    if (query.trim()) {
      return undefined;
    }
    return selectedCategoryFilter.key === 'all'
      ? ['library-books', 'account-billing']
      : selectedCategoryFilter.categoryKeys;
  }, [query, selectedCategoryFilter]);
  const activeArticleCategoryKey = useMemo(() => (
    activeSlug ? articles.find((article) => article.slug === activeSlug)?.categoryKey : undefined
  ), [activeSlug, articles]);
  const resolvedOpenArticleCategories = useMemo(() => (
    activeArticleCategoryKey && !openArticleCategories.includes(activeArticleCategoryKey)
      ? [...openArticleCategories, activeArticleCategoryKey]
      : openArticleCategories
  ), [activeArticleCategoryKey, openArticleCategories]);
  const articleDisplayMode = query.trim() ? 'search' : 'browse';
  const visibleCategoryKeys = useMemo(() => {
    const visibleCategorySet = new Set(visibleArticles.map((article) => article.categoryKey));
    return supportArticleCategories
      .map((category) => category.key)
      .filter((categoryKey) => visibleCategorySet.has(categoryKey));
  }, [visibleArticles]);

  useEffect(() => {
    let nextOpenCategories: string[];
    if (query.trim()) {
      nextOpenCategories = visibleCategoryKeys;
    } else {
      nextOpenCategories = defaultOpenCategories || [];
    }
    if (activeArticleCategoryKey && !nextOpenCategories.includes(activeArticleCategoryKey)) {
      nextOpenCategories = [...nextOpenCategories, activeArticleCategoryKey];
    }
    setOpenArticleCategories((current) => (
      areStringArraysEqual(current, nextOpenCategories) ? current : nextOpenCategories
    ));
  }, [activeArticleCategoryKey, defaultOpenCategories, query, visibleCategoryKeys]);

  return (
    <main className="support-page-shell" data-testid="tutorial:support:page">
      <div className="support-page-inner">
        <AppPageHeader
          className="support-hero"
          variant="banner"
          title="Support"
          titleId="support-title"
          subtitle="Find support articles, tutorials, FAQs, and guidelines in one place. If you need a human, choose a channel below."
          actions={(
            <button
              type="button"
              onClick={startSupportTour}
              data-tour-id="support-tour-trigger"
              className="support-tour-link support-tour-link--header app-tour-trigger"
            >
              <QuestionCircleOutlined />
              <span>Take a Tour</span>
            </button>
          )}
        />

        <section className="support-contact-grid support-contact-grid--top" aria-label="Contact support channels" data-testid="tutorial:support:contact-channels">
          {contactChannels.map((channel) => (
            <a
              key={channel.key}
              href={channel.href}
              target={channel.key === 'email' ? undefined : '_blank'}
              rel={channel.key === 'email' ? undefined : 'noopener noreferrer'}
              className="support-contact-card"
              data-testid={`tutorial:support:channel-${channel.key}`}
            >
              <span className="support-contact-card-icon">{channel.icon}</span>
              <span>
                <strong>{channel.title}</strong>
                <small>
                  {channel.key === 'email'
                    ? 'Get a reply in your inbox. Best for account or billing questions. Email: support@scribeshadow.com'
                    : channel.key === 'discord'
                    ? 'Chat with the team and community. Best for quick help.'
                    : 'Ask questions and share tips with other authors.'}
                </small>
              </span>
              <em>{channel.key === 'facebook' ? 'Visit Facebook group' : channel.cta}</em>
            </a>
          ))}
        </section>

        <p><a href="/support/request">Optional: submit an online support request</a></p>

        <nav className="support-tabbar" aria-label="Support sections" data-testid="tutorial:support:tabs">
          {[
            ['articles', 'Support Articles'],
            ['tutorials', 'Tutorials'],
            ['faq', 'FAQ'],
            ['guidelines', 'Guidelines'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={activeSupportTab === key ? 'support-tab support-tab--active' : 'support-tab'}
              data-testid={`tutorial:support:${key}-tab`}
              onClick={() => setActiveSupportTab(key as SupportTabKey)}
            >
              {label}
            </button>
          ))}
        </nav>

        {activeSupportTab === 'articles' && (
        <>
        <section className="support-search-card" aria-label="Search support" data-tour-id="support-search" data-testid="tutorial:support:search">
          <Input
            size="large"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search uploads, credits, errors, exports..."
            value={query}
            onChange={handleSearchChange}
            aria-label="Search support"
          />
        </section>

        <section className="support-section-card" aria-labelledby="support-featured-title" data-tour-id="support-featured" data-testid="tutorial:support:featured">
          <div className="support-section-heading">
            <div>
              <span className="support-kicker">Start Here</span>
              <h2 id="support-featured-title">Top answers</h2>
              <p>Jump straight to the issues authors look up most often.</p>
            </div>
          </div>
          <div className="support-quick-link-grid">
            {featuredLinks.map((link) => (
              <button
                type="button"
                key={link.title}
                className="support-quick-link"
                onClick={() => openArticle(link.articleSlug, link.sectionId || 'support-articles')}
              >
                <span className="support-quick-link-icon">{link.icon}</span>
                <span className="support-quick-link-copy">
                  <span>{link.title}</span>
                  <small>{link.description}</small>
                </span>
              </button>
            ))}
          </div>
        </section>
        </>
        )}

        {activeSupportTab === 'articles' && (
        <section
          className="support-section-card"
          id="support-articles"
          data-tour-id="support-articles"
          data-testid="tutorial:support:articles"
          tabIndex={-1}
          aria-labelledby="support-articles-title"
        >
          <div className="support-section-heading">
            <div>
              <span className="support-kicker">Articles</span>
              <h2 id="support-articles-title">Browse support articles</h2>
              <p>Articles are grouped by workflow and stay searchable by title, summary, section text, and list text.</p>
            </div>
            {!loading && !error && (
              <div className="support-article-heading-tools">
                <span className="support-article-toolbar-label">{resultLabel}</span>
                {!query.trim() && (
                  <span className="support-article-toolbar-actions">
                    <button
                      type="button"
                      className="support-article-toolbar-button"
                      onClick={() => setOpenArticleCategories(visibleCategoryKeys)}
                    >
                      Expand all
                    </button>
                    <button
                      type="button"
                      className="support-article-toolbar-button"
                      onClick={() => setOpenArticleCategories([])}
                    >
                      Collapse all
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>
          {!query.trim() && (
            <div className="support-category-filter-list" aria-label="Filter support article categories">
              {categoryFilters.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  className={
                    filter.key === activeCategoryFilter
                      ? 'support-category-filter-chip support-category-filter-chip--active'
                      : 'support-category-filter-chip'
                  }
                  onClick={() => setCategoryFilter(filter.key)}
                  aria-pressed={filter.key === activeCategoryFilter}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          )}
          {error && <Alert message={error} type="warning" showIcon />}
          {!error && loading && (
            <div className="support-state-panel">
              <Text type="secondary">Loading support articles...</Text>
            </div>
          )}
          {!error && !loading && (
            <SupportArticleResults
              key={query.trim() ? 'search' : 'browse'}
              articles={visibleArticles}
              query={query}
              displayMode={articleDisplayMode}
              activeSlug={activeSlug}
              highlightedSlug={highlightedArticleSlug}
              onActiveSlugChange={handleActiveSlugChange}
              defaultOpenCategories={defaultOpenCategories}
              openCategoryKeys={resolvedOpenArticleCategories}
              onOpenCategoryKeysChange={setOpenArticleCategories}
              resultLabel={resultLabel}
              showToolbar={Boolean(query.trim())}
              emptyMessage="Nothing matched your search. Try shorter terms like credits, upload, stuck, French, referral, or export."
            />
          )}
        </section>
        )}

        {activeSupportTab === 'tutorials' && (
        <section
          className="support-section-card"
          id="support-tutorials"
          data-tour-id="support-tutorials"
          data-testid="tutorial:support:tutorials"
          tabIndex={-1}
          aria-labelledby="support-tutorials-title"
        >
          <div className="support-section-heading">
            <div>
              <span className="support-kicker">Tutorials</span>
              <h2 id="support-tutorials-title">Video tutorials</h2>
              <p>Short walkthroughs for common ScribeShadow workflows.</p>
            </div>
          </div>
          <div className="support-tutorial-grid">
            {tutorials.map((tutorial, index) => (
              <a
                key={tutorial.id}
                href={tutorial.url}
                target="_blank"
                rel="noopener noreferrer"
                className="support-tutorial-card"
              >
                <img
                  src={`https://img.youtube.com/vi/${tutorial.id}/hqdefault.jpg`}
                  alt=""
                  loading="lazy"
                />
                <span className="support-tutorial-index">{index + 1}</span>
                <span className="support-tutorial-copy">
                  <strong>{tutorial.title}</strong>
                  <span><PlayCircleOutlined /> Watch on YouTube{tutorial.duration ? ` · ${tutorial.duration}` : ''}</span>
                </span>
              </a>
            ))}
          </div>
          <div className="support-tutorial-group">
            <div className="support-tutorial-group-heading">
              <h3>Advanced</h3>
              <p>Deeper walkthroughs for specialized tools and higher-control workflows.</p>
            </div>
            <div className="support-tutorial-grid">
              {advancedTutorials.map((tutorial, index) => (
                <a
                  key={tutorial.id}
                  href={tutorial.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="support-tutorial-card"
                >
                  <img
                    src={`https://img.youtube.com/vi/${tutorial.id}/hqdefault.jpg`}
                    alt=""
                    loading="lazy"
                  />
                  <span className="support-tutorial-index">{tutorials.length + index + 1}</span>
                  <span className="support-tutorial-copy">
                    <strong>{tutorial.title}</strong>
                    <span><PlayCircleOutlined /> Watch on YouTube{tutorial.duration ? ` · ${tutorial.duration}` : ''}</span>
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>
        )}

        {activeSupportTab === 'faq' && (
          <section
            className="support-section-card"
            id="support-faq"
            data-testid="tutorial:support:faq"
            tabIndex={-1}
            aria-labelledby="support-faq-title"
          >
            <div className="support-section-heading">
              <div>
                <span className="support-kicker">FAQ</span>
                <h2 id="support-faq-title">Frequently asked questions</h2>
                <p>Browse common questions from the support article library.</p>
              </div>
            </div>
            <SupportArticleResults
              articles={faqArticles}
              query=""
              displayMode="browse"
              activeSlug={activeSlug}
              highlightedSlug={highlightedArticleSlug}
              onActiveSlugChange={handleActiveSlugChange}
              defaultOpenCategories={defaultOpenCategories}
              openCategoryKeys={resolvedOpenArticleCategories}
              onOpenCategoryKeysChange={setOpenArticleCategories}
              resultLabel={`${faqArticles.length} FAQs`}
              showToolbar
              emptyMessage="No FAQ articles are available right now."
            />
          </section>
        )}

        {activeSupportTab === 'guidelines' && (
          <>
            <section
              className="support-section-card"
              id="word-formatting-guidelines"
              data-testid="tutorial:support:guidelines"
              tabIndex={-1}
              aria-labelledby="support-guidelines-title"
            >
          <div className="support-section-heading">
            <div>
              <span className="support-kicker">Guidelines</span>
              <h2 id="support-guidelines-title">Word formatting guidelines</h2>
              <p>Prepare Word documents so import, translation, and export stay predictable.</p>
            </div>
          </div>
          <div className="support-guideline-grid">
            <div className="support-guideline-item">
              <strong>Use DOCX</strong>
              <span>Files in `.doc` are not supported. EPUB is best when the structure is already final.</span>
            </div>
            <div className="support-guideline-item">
              <strong>Mark chapters clearly</strong>
              <span>Apply Word’s built-in Heading 1 paragraph style to each chapter heading, or insert a real page break before each chapter.</span>
            </div>
            <div className="support-guideline-item">
              <strong>Verify Word recognized them</strong>
              <span>Open Word’s Navigation Pane and confirm every chapter appears there. Changing only the font, size, or alignment does not create a heading.</span>
            </div>
            <div className="support-guideline-item">
              <strong>Keep two-line headings together</strong>
              <span>Use Heading 1 on the chapter line and Normal, Subtitle, or Heading 2 on the second line. You can also use Shift+Enter inside one Heading 1 paragraph.</span>
            </div>
            <div className="support-guideline-item">
              <strong>Select the source language</strong>
              <span>Choose the current manuscript language during upload so translation setup is correct.</span>
            </div>
            <div className="support-guideline-item">
              <strong>Skip tables of contents</strong>
              <span>Page numbers shift during translation, so recreate a table of contents after export if needed.</span>
            </div>
            <div className="support-guideline-item">
              <strong>Review complex layouts</strong>
              <span>Section breaks, headers, footers, and decorative formatting can still need cleanup after export.</span>
            </div>
            <div className="support-guideline-item">
              <strong>Spot-check final files</strong>
              <span>Review paragraph spacing, scene breaks, and special characters before delivery.</span>
            </div>
            <div className="support-guideline-item">
              <strong>Acceptable use</strong>
              <span>Use ScribeShadow only for books and metadata you have the rights to process, and do not upload unlawful, abusive, or privacy-invasive content.</span>
            </div>
            <div className="support-guideline-item">
              <strong>Policy review</strong>
              <span>Contact support for current terms, privacy, and acceptable-use details.</span>
            </div>
          </div>
            </section>

            <section
              className="support-section-card"
              id="support-contact"
              data-tour-id="support-contact"
              data-testid="tutorial:support:contact"
              tabIndex={-1}
              aria-labelledby="support-contact-title"
            >
          <div className="support-section-heading">
            <div>
              <span className="support-kicker">Contact</span>
              <h2 id="support-contact-title">Need a human?</h2>
              <p>Include your account email, book title, and any support code shown in the app.</p>
            </div>
          </div>
          <div className="support-contact-prep" aria-label="Information to include with support requests">
            <strong>Include:</strong>
            {supportPrepItems.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
          <div className="support-contact-grid">
            {contactChannels.map((channel) => (
              <a
                key={channel.key}
                href={channel.href}
                target={channel.key === 'email' ? undefined : '_blank'}
                rel={channel.key === 'email' ? undefined : 'noopener noreferrer'}
                className="support-contact-card"
                data-testid={`tutorial:support:contact-channel-${channel.key}`}
              >
                <span className="support-contact-card-icon">{channel.icon}</span>
                <span>
                  <strong>{channel.title}</strong>
                  <small>{channel.description}</small>
                </span>
                <em>{channel.cta}</em>
              </a>
            ))}
          </div>
            </section>
          </>
        )}
        <AppTour
          open={supportTour.open}
          steps={supportTourSteps}
          current={supportTour.current}
          onChange={handleSupportTourChange}
          onClose={supportTour.close}
          onFinish={supportTour.close}
        />
      </div>
    </main>
  );
};

export default Support;
